import { formatValueAsDate } from '@shared/helpers';
import { FormatUnitType, TeamRankInfo } from 'types';
import {
  formatFumbles,
  formatInterceptions,
  formatPercentage,
  formatPlays,
  formatPoints,
  formatTDs,
  formatYards,
} from './StringHelpers';

/**
 * Returns the length of the bar based on the rank or value of the team
 *
 * @param {TeamRankInfo} rankInfo - TeamRankInfo object
 * @param {'Rank' | 'Value'} chartBy - Chart sizing by Rank (1-32) or Value (normalized range / value)
 *
 * @returns {string} lengthValue - Length of the bar in pixels
 */
export const calculateLengthValue = (rankInfo: TeamRankInfo, chartBy: 'Rank' | 'Value', maxWidth = 250) => {
  if (chartBy === 'Rank' && rankInfo.teamRank) {
    const rankNormalized = rankInfo.teamRank - 1;
    // rankInfo.highOrLow === 'High' ? 33 - rankInfo.teamRank : rankInfo.teamRank - 1;
    const oneRankLength = maxWidth / 31;
    const lengthValue =
      rankInfo.highOrLow === 'High'
        ? `${Math.round(maxWidth - oneRankLength * rankNormalized)}px`
        : `${Math.round(oneRankLength * rankNormalized + oneRankLength)}px`;
    return lengthValue;
  } else if (chartBy === 'Value' && rankInfo.teamValue >= 0 && rankInfo.bestTeam && rankInfo.worstTeam) {
    // console.log('RankInfo', rankInfo);
    const bestValue = rankInfo.bestAvg ?? rankInfo.bestTeam.value;
    const worstValue = rankInfo.worstAvg ?? rankInfo.worstTeam.value;
    const valueRange =
      rankInfo.bestAvg && rankInfo.worstAvg
        ? Math.max(rankInfo.worstAvg, rankInfo.bestAvg)
        : Math.max(worstValue, bestValue);
    const valueNormalized = valueRange - rankInfo.teamValue;
    const oneValueWidth = maxWidth / valueRange;

    // const isBestGreaterThanWorst = bestValue >= worstValue;
    const teamValue = Math.min(
      rankInfo.teamValue === 0 ? 0 : Math.round((maxWidth - oneValueWidth * valueNormalized) * 100) / 100,
      maxWidth + 5,
    );
    return `${teamValue}px`;
    // This was an attempt to make first place the longest bar, when best team has the smaller value like least yards allowed
    //  but it didn't work out as expected.
    // if (isBestGreaterThanWorst) {
    //   return `${teamValue}px`;
    // } else {
    //   return `${285 - teamValue}px`;
    // }
  }

  return '0';
};

const getTeamValue = (teamValue: number) =>
  Math.ceil(teamValue) !== Math.floor(teamValue) ? teamValue.toFixed(1) : teamValue;

export const formatTeamValue = (
  formatUnitType: FormatUnitType,
  ri: TeamRankInfo,
  divisor: number = 1,
  rankCategory?: string,
) => {
  switch (formatUnitType) {
    case 'time':
      return formatValueAsDate(ri.teamValue, divisor);
    case 'percent':
      return formatPercentage(ri.teamValue);
    case 'points':
      return formatPoints(ri.teamValue, divisor);
    case 'ratio':
      return getTeamValue(ri.teamValue);
    case 'plays':
      return formatPlays(ri.teamValue, rankCategory);
    case 'yards':
      return formatYards(ri.teamValue, divisor);
    case 'int':
      return formatInterceptions(ri.teamValue, divisor);
    case 'fum':
      return formatFumbles(ri.teamValue, divisor);
    case 'tds':
      return formatTDs(ri.teamValue, divisor);
    default:
      return getTeamValue(ri.teamValue / divisor);
  }
};
